import { api } from "@/shared/api";

export type ShortIdEntity = "ASSET" | "PART" | "LOCATION";

export interface RegisterShortIdBody {
  entity: ShortIdEntity;
  oldId: string;
}

export interface RegisterShortIdResponse {
  newSmallId: string;
}

export interface ShortIdLookupResponse {
  entity: ShortIdEntity;
  oldId: string;
}

function normalizeRegisterResponse(raw: unknown): RegisterShortIdResponse {
  if (raw && typeof raw === "object" && "newSmallId" in (raw as object)) {
    return { newSmallId: String((raw as RegisterShortIdResponse).newSmallId) };
  }
  if (raw && typeof raw === "object" && "data" in (raw as object)) {
    return normalizeRegisterResponse((raw as { data: unknown }).data);
  }
  throw new Error("Invalid short-ids register response");
}

function normalizeLookupResponse(raw: unknown): ShortIdLookupResponse {
  if (
    raw &&
    typeof raw === "object" &&
    "entity" in (raw as object) &&
    "oldId" in (raw as object)
  ) {
    const o = raw as ShortIdLookupResponse;
    return { entity: o.entity, oldId: String(o.oldId) };
  }
  if (raw && typeof raw === "object" && "data" in (raw as object)) {
    return normalizeLookupResponse((raw as { data: unknown }).data);
  }
  throw new Error("Invalid short-ids lookup response");
}

export function shortIdRegisterQueryKey(entity: ShortIdEntity, oldId: string) {
  return ["shortIds", "register", entity, oldId] as const;
}

export const shortIdsService = {
  /** POST /short-ids/register — idempotent; returns existing mapping if any */
  async register(body: RegisterShortIdBody): Promise<RegisterShortIdResponse> {
    const { data } = await api.post<unknown>("/short-ids/register", body);
    return normalizeRegisterResponse(data);
  },

  /** GET /short-ids/by-short-id/:shortId */
  async getByShortId(shortId: string): Promise<ShortIdLookupResponse> {
    const encoded = encodeURIComponent(shortId.trim());
    const { data } = await api.get<unknown>(`/short-ids/by-short-id/${encoded}`);
    return normalizeLookupResponse(data);
  },
};
